import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  Menu,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  Delete,
  Edit,
  FormatListBulleted,
  MoreVert,
  Schedule,
  TableChart,
  Warning,
} from "@mui/icons-material";
import {
  useGetMilestonesQuery,
  useCreateMilestoneMutation,
  useUpdateMilestoneMutation,
  useDeleteMilestoneMutation,
  ProTrackMilestone,
  MilestoneCategory,
  MilestoneStatus,
} from "../../../redux/protrackApi";
import { useGetComsBlocksQuery } from "../../../redux/comsApi";

const NAVY = "#123456";
const TODAY = new Date().toISOString().slice(0, 10);

const CATEGORIES: { value: MilestoneCategory; label: string }[] = [
  { value: "FOUNDATION", label: "Foundation" },
  { value: "SUPERSTRUCTURE", label: "Superstructure" },
  { value: "ROOFING", label: "Roofing" },
  { value: "BRICKWORK", label: "Brickwork" },
  { value: "PLASTERING", label: "Plastering" },
  { value: "ELECTRICAL", label: "Electrical" },
  { value: "PLUMBING", label: "Plumbing" },
  { value: "FINISHES", label: "Finishes" },
  { value: "LANDSCAPING", label: "Landscaping" },
  { value: "COMMISSIONING", label: "Commissioning" },
  { value: "OTHER", label: "Other" },
];

const STATUS_CONFIG: Record<MilestoneStatus, { label: string; color: string }> = {
  PLANNED: { label: "Planned", color: "#78909c" },
  IN_PROGRESS: { label: "In Progress", color: "#1565c0" },
  COMPLETED: { label: "Completed", color: "#2e7d32" },
  DELAYED: { label: "Delayed", color: "#f57c00" },
  CANCELLED: { label: "Cancelled", color: "#b71c1c" },
};

const emptyForm = (): Partial<ProTrackMilestone> => ({
  blockId: "",
  title: "",
  category: "OTHER",
  plannedStart: TODAY,
  plannedEnd: TODAY,
  actualStart: "",
  actualEnd: "",
  status: "PLANNED",
  progressPct: 0,
  responsible: "",
  notes: "",
});

function resolveStatus(m: Partial<ProTrackMilestone>): MilestoneStatus {
  const s = m.status as string | undefined;
  if (!s || s === "PLANNED" || s === "IN_PROGRESS") {
    if (m.plannedEnd && m.plannedEnd < TODAY && s !== "COMPLETED" && s !== "CANCELLED") {
      return "DELAYED";
    }
  }
  return (s as MilestoneStatus) ?? "PLANNED";
}

// ─── Gantt helpers ────────────────────────────────────────────────────────────

function addDaysToDate(dateStr: string, days: number): string {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function daysBetween(a: string, b: string): number {
  return Math.round(
    (new Date(b + "T00:00:00").getTime() - new Date(a + "T00:00:00").getTime()) / 86400000
  );
}

function formatDateShort(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-ZA", { day: "2-digit", month: "short" });
}

const GANTT_ROW_H = 38;
const GANTT_LABEL_W = 220;
const DAY_PX = 3.2;

interface GanttChartProps {
  milestones: ProTrackMilestone[];
  onEdit: (m: ProTrackMilestone) => void;
  onDeleteClick: (m: ProTrackMilestone) => void;
}

const GanttChart: React.FC<GanttChartProps> = ({ milestones, onEdit }) => {
  if (milestones.length === 0) return null;

  const allDates = milestones.flatMap((m) =>
    [m.plannedStart, m.plannedEnd, m.actualStart, m.actualEnd].filter(Boolean) as string[]
  );
  const minDate = allDates.reduce((a, b) => (a < b ? a : b));
  const maxDate = allDates.reduce((a, b) => (a > b ? a : b));
  const chartStart = addDaysToDate(minDate, -7);
  const chartEnd = addDaysToDate(maxDate, 7);
  const totalDays = daysBetween(chartStart, chartEnd);
  const chartW = Math.max(300, totalDays * DAY_PX);

  // Month header ticks
  const months: { label: string; x: number; width: number }[] = [];
  let cursor = new Date(chartStart + "T00:00:00");
  const endD = new Date(chartEnd + "T00:00:00");
  while (cursor <= endD) {
    const y = cursor.getFullYear();
    const mo = cursor.getMonth();
    const firstOfNext = new Date(y, mo + 1, 1);
    const mStart = cursor < new Date(chartStart + "T00:00:00") ? new Date(chartStart + "T00:00:00") : cursor;
    const mEnd = firstOfNext > endD ? endD : firstOfNext;
    const x = daysBetween(chartStart, mStart.toISOString().slice(0, 10)) * DAY_PX;
    const width = Math.max(0, daysBetween(mStart.toISOString().slice(0, 10), mEnd.toISOString().slice(0, 10)) * DAY_PX);
    months.push({
      label: new Date(y, mo, 1).toLocaleDateString("en-ZA", { month: "short", year: "2-digit" }),
      x,
      width,
    });
    cursor = firstOfNext;
  }

  const todayX = daysBetween(chartStart, TODAY) * DAY_PX;

  return (
    <Box sx={{ overflowX: "auto", border: "1px solid #e0e0e0", borderRadius: 2 }}>
      <Box sx={{ display: "flex", minWidth: GANTT_LABEL_W + chartW }}>
        {/* Label column */}
        <Box sx={{ width: GANTT_LABEL_W, flexShrink: 0, borderRight: "1px solid #e0e0e0" }}>
          <Box sx={{ height: 32, bgcolor: NAVY, display: "flex", alignItems: "center", px: 1.5, borderBottom: "1px solid rgba(255,255,255,0.2)" }}>
            <Typography variant="caption" fontWeight={700} color="#fff">Milestone</Typography>
          </Box>
          {milestones.map((m, i) => {
            const sc = STATUS_CONFIG[m.status as MilestoneStatus] ?? STATUS_CONFIG.PLANNED;
            const isLate = m.status !== "COMPLETED" && m.status !== "CANCELLED" && m.plannedEnd < TODAY;
            return (
              <Box
                key={m.id}
                sx={{ height: GANTT_ROW_H, display: "flex", alignItems: "center", px: 1.5, borderBottom: i < milestones.length - 1 ? "1px solid #f0f0f0" : undefined, bgcolor: i % 2 === 0 ? "#fafafa" : "#fff", gap: 0.5 }}
              >
                <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: sc.color, flexShrink: 0 }} />
                <Tooltip title={m.title} placement="right">
                  <Typography variant="caption" noWrap sx={{ flex: 1, fontWeight: 500, cursor: "pointer" }} onClick={() => onEdit(m)}>
                    {m.title}
                  </Typography>
                </Tooltip>
                {isLate && <Warning sx={{ fontSize: 12, color: "#f57c00", flexShrink: 0 }} />}
              </Box>
            );
          })}
        </Box>

        {/* Chart area */}
        <Box sx={{ flex: 1, position: "relative", minWidth: chartW }}>
          {/* Month header */}
          <Box sx={{ height: 32, display: "flex", position: "relative", bgcolor: NAVY, borderBottom: "1px solid rgba(255,255,255,0.15)" }}>
            {months.map((mo, i) => (
              <Box
                key={i}
                sx={{ position: "absolute", left: mo.x, width: mo.width, height: 32, display: "flex", alignItems: "center", justifyContent: "center", borderRight: "1px solid rgba(255,255,255,0.2)", overflow: "hidden" }}
              >
                <Typography variant="caption" fontWeight={700} color="#fff" noWrap>{mo.label}</Typography>
              </Box>
            ))}
          </Box>

          {/* Rows */}
          {milestones.map((m, i) => {
            const sc = STATUS_CONFIG[m.status as MilestoneStatus] ?? STATUS_CONFIG.PLANNED;
            const plannedLeft = daysBetween(chartStart, m.plannedStart) * DAY_PX;
            const plannedW = Math.max(6, daysBetween(m.plannedStart, m.plannedEnd) * DAY_PX);
            const aEnd = m.actualStart ? (m.actualEnd ?? TODAY) : null;
            const actualLeft = m.actualStart ? daysBetween(chartStart, m.actualStart) * DAY_PX : null;
            const actualW = m.actualStart && aEnd ? Math.max(4, daysBetween(m.actualStart, aEnd) * DAY_PX) : null;

            return (
              <Box key={m.id} sx={{ height: GANTT_ROW_H, position: "relative", bgcolor: i % 2 === 0 ? "#fafafa" : "#fff", borderBottom: i < milestones.length - 1 ? "1px solid #f0f0f0" : undefined }}>
                {/* Planned bar */}
                <Tooltip title={`Planned: ${formatDateShort(m.plannedStart)} – ${formatDateShort(m.plannedEnd)} · ${m.progressPct}%`}>
                  <Box
                    onClick={() => onEdit(m)}
                    sx={{ position: "absolute", left: plannedLeft, top: "50%", transform: "translateY(-50%)", width: plannedW, height: 16, bgcolor: `${sc.color}40`, border: `1.5px solid ${sc.color}`, borderRadius: 1, cursor: "pointer", overflow: "hidden" }}
                  >
                    <Box sx={{ width: `${m.progressPct}%`, height: "100%", bgcolor: sc.color, opacity: 0.9 }} />
                  </Box>
                </Tooltip>
                {/* Actual bar */}
                {actualLeft !== null && actualW !== null && (
                  <Tooltip title={`Actual: ${formatDateShort(m.actualStart!)} – ${m.actualEnd ? formatDateShort(m.actualEnd) : "ongoing"}`}>
                    <Box sx={{ position: "absolute", left: actualLeft, top: "76%", width: actualW, height: 4, bgcolor: sc.color, borderRadius: 1, opacity: 0.8 }} />
                  </Tooltip>
                )}
              </Box>
            );
          })}

          {/* Today line */}
          {todayX >= 0 && todayX <= chartW && (
            <Box sx={{ position: "absolute", top: 0, left: todayX, width: 1.5, height: "100%", bgcolor: "#e53935", zIndex: 10, pointerEvents: "none" }}>
              <Box sx={{ position: "absolute", top: 0, left: -16, width: 33, textAlign: "center" }}>
                <Typography variant="caption" sx={{ bgcolor: "#e53935", color: "#fff", px: 0.5, borderRadius: 0.5, fontSize: 9 }}>TODAY</Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

export const ProTrackProgramme: React.FC = () => {
  const { data: blocks = [] } = useGetComsBlocksQuery();
  const [blockFilter, setBlockFilter] = useState("");
  const [viewMode, setViewMode] = useState<"list" | "gantt">("list");

  const { data: milestones = [], isLoading } = useGetMilestonesQuery({ blockId: blockFilter || undefined });
  const [createMilestone, { isLoading: creating }] = useCreateMilestoneMutation();
  const [updateMilestone, { isLoading: updating }] = useUpdateMilestoneMutation();
  const [deleteMilestone] = useDeleteMilestoneMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ProTrackMilestone | null>(null);
  const [form, setForm] = useState<Partial<ProTrackMilestone>>(emptyForm());
  const [formError, setFormError] = useState("");

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuTarget, setMenuTarget] = useState<ProTrackMilestone | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const openCreate = () => { setEditTarget(null); setForm(emptyForm()); setFormError(""); setDialogOpen(true); };
  const openEdit = (m: ProTrackMilestone) => { setMenuAnchor(null); setEditTarget(m); setForm({ ...m }); setFormError(""); setDialogOpen(true); };

  const handleSave = async () => {
    if (!form.blockId) { setFormError("Select a project block."); return; }
    if (!form.title?.trim()) { setFormError("Title is required."); return; }
    if (!form.plannedStart || !form.plannedEnd) { setFormError("Planned dates are required."); return; }
    setFormError("");
    const payload = {
      blockId: form.blockId!,
      title: form.title!,
      category: (form.category as MilestoneCategory) ?? "OTHER",
      plannedStart: form.plannedStart!,
      plannedEnd: form.plannedEnd!,
      actualStart: form.actualStart || undefined,
      actualEnd: form.actualEnd || undefined,
      status: resolveStatus(form),
      progressPct: Number(form.progressPct ?? 0),
      responsible: form.responsible || undefined,
      notes: form.notes || undefined,
    };
    try {
      if (editTarget?.id) {
        await updateMilestone({ id: editTarget.id, data: payload }).unwrap();
      } else {
        await createMilestone(payload).unwrap();
      }
      setDialogOpen(false);
    } catch { setFormError("Failed to save. Please try again."); }
  };

  const handleDelete = async () => {
    if (menuTarget?.id) await deleteMilestone(menuTarget.id);
    setDeleteConfirmOpen(false);
    setMenuAnchor(null);
  };

  const grouped = useMemo(
    () => milestones.reduce<Record<string, ProTrackMilestone[]>>((acc, m) => {
      const key = m.blockName ?? m.blockId;
      (acc[key] = acc[key] ?? []).push(m);
      return acc;
    }, {}),
    [milestones]
  );

  return (
    <Box>
      {/* Toolbar */}
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={3}>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Filter by Block</InputLabel>
            <Select label="Filter by Block" value={blockFilter} onChange={(e) => setBlockFilter(e.target.value)}>
              <MenuItem value="">All Blocks</MenuItem>
              {blocks.map((b) => <MenuItem key={b.id} value={b.id}>{b.code} — {b.name}</MenuItem>)}
            </Select>
          </FormControl>
          <ToggleButtonGroup size="small" value={viewMode} exclusive onChange={(_, v) => v && setViewMode(v)}>
            <ToggleButton value="list"><Tooltip title="List view"><FormatListBulleted sx={{ fontSize: 18 }} /></Tooltip></ToggleButton>
            <ToggleButton value="gantt"><Tooltip title="Gantt chart"><TableChart sx={{ fontSize: 18 }} /></Tooltip></ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate} sx={{ bgcolor: NAVY, "&:hover": { bgcolor: "#1a4a7a" } }}>
          Add Milestone
        </Button>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : milestones.length === 0 ? (
        <Box textAlign="center" py={8} color="text.secondary">
          <Schedule sx={{ fontSize: 48, opacity: 0.3 }} />
          <Typography mt={1}>No milestones found. Add the first one.</Typography>
        </Box>
      ) : (
        Object.entries(grouped).map(([groupName, items]) => (
          <Box key={groupName} mb={4}>
            <Typography variant="subtitle1" fontWeight={700} color={NAVY} mb={1}>{groupName}</Typography>

            {viewMode === "gantt" ? (
              <GanttChart milestones={items} onEdit={openEdit} onDeleteClick={(m) => { setMenuTarget(m); setDeleteConfirmOpen(true); }} />
            ) : (
              <Box display="flex" flexDirection="column" gap={1.5}>
                {items.map((m) => {
                  const sc = STATUS_CONFIG[m.status as MilestoneStatus] ?? STATUS_CONFIG.PLANNED;
                  const isLate = m.status !== "COMPLETED" && m.status !== "CANCELLED" && m.plannedEnd < TODAY;
                  return (
                    <Box key={m.id} sx={{ border: "1px solid #e0e0e0", borderLeft: `4px solid ${sc.color}`, borderRadius: 2, p: 2, bgcolor: "#fff", "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.08)" } }}>
                      <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                        <Box flex={1}>
                          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <Typography fontWeight={700}>{m.title}</Typography>
                            <Chip label={sc.label} size="small" sx={{ bgcolor: `${sc.color}22`, color: sc.color, fontWeight: 600, fontSize: 11 }} />
                            <Chip label={CATEGORIES.find((c) => c.value === m.category)?.label ?? m.category} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                            {isLate && <Chip icon={<Warning sx={{ fontSize: 14 }} />} label="Overdue" size="small" sx={{ bgcolor: "#fff3e0", color: "#f57c00", fontWeight: 600, fontSize: 11 }} />}
                          </Box>
                          <Box display="flex" gap={3} mt={1} flexWrap="wrap">
                            <Typography variant="caption" color="text.secondary">Planned: {m.plannedStart} → {m.plannedEnd}</Typography>
                            {m.actualStart && <Typography variant="caption" color="text.secondary">Actual: {m.actualStart} → {m.actualEnd ?? "ongoing"}</Typography>}
                            {m.responsible && <Typography variant="caption" color="text.secondary">Responsible: {m.responsible}</Typography>}
                          </Box>
                          <Box display="flex" alignItems="center" gap={1} mt={1.5}>
                            <LinearProgress variant="determinate" value={m.progressPct} sx={{ width: 160, height: 6, borderRadius: 3, bgcolor: "#e0e0e0", "& .MuiLinearProgress-bar": { bgcolor: sc.color } }} />
                            <Typography variant="caption" fontWeight={700} color={sc.color}>{m.progressPct}%</Typography>
                          </Box>
                        </Box>
                        <IconButton size="small" onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuTarget(m); }}><MoreVert /></IconButton>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        ))
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={() => menuTarget && openEdit(menuTarget)}><Edit sx={{ mr: 1, fontSize: 18 }} /> Edit</MenuItem>
        <MenuItem sx={{ color: "error.main" }} onClick={() => { setDeleteConfirmOpen(true); setMenuAnchor(null); }}><Delete sx={{ mr: 1, fontSize: 18 }} /> Delete</MenuItem>
      </Menu>

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs">
        <DialogTitle>Delete Milestone</DialogTitle>
        <DialogContent><Typography>Delete <strong>{menuTarget?.title}</strong>? This cannot be undone.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editTarget ? "Edit Milestone" : "Add Milestone"}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <FormControl fullWidth size="small" required>
              <InputLabel>Project Block *</InputLabel>
              <Select label="Project Block *" value={form.blockId ?? ""} onChange={(e) => setForm((f) => ({ ...f, blockId: e.target.value }))}>
                {blocks.map((b) => <MenuItem key={b.id} value={b.id}>{b.code} — {b.name}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Title *" size="small" fullWidth value={form.title ?? ""} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select label="Category" value={form.category ?? "OTHER"} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value as MilestoneCategory }))}>
                {CATEGORIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
              </Select>
            </FormControl>
            <Box display="flex" gap={2}>
              <TextField label="Planned Start *" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={form.plannedStart ?? ""} onChange={(e) => setForm((f) => ({ ...f, plannedStart: e.target.value }))} />
              <TextField label="Planned End *" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={form.plannedEnd ?? ""} onChange={(e) => setForm((f) => ({ ...f, plannedEnd: e.target.value }))} />
            </Box>
            <Box display="flex" gap={2}>
              <TextField label="Actual Start" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={form.actualStart ?? ""} onChange={(e) => setForm((f) => ({ ...f, actualStart: e.target.value }))} />
              <TextField label="Actual End" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={form.actualEnd ?? ""} onChange={(e) => setForm((f) => ({ ...f, actualEnd: e.target.value }))} />
            </Box>
            <Box display="flex" gap={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={form.status ?? "PLANNED"} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as MilestoneStatus }))}>
                  {Object.entries(STATUS_CONFIG).map(([v, sc]) => <MenuItem key={v} value={v}>{sc.label}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField label="Progress %" type="number" size="small" sx={{ width: 120 }} inputProps={{ min: 0, max: 100 }} value={form.progressPct ?? 0} onChange={(e) => setForm((f) => ({ ...f, progressPct: Number(e.target.value) }))} />
            </Box>
            <TextField label="Responsible" size="small" fullWidth value={form.responsible ?? ""} onChange={(e) => setForm((f) => ({ ...f, responsible: e.target.value }))} />
            <TextField label="Notes" size="small" fullWidth multiline rows={2} value={form.notes ?? ""} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
            {formError && <Typography color="error" variant="caption">{formError}</Typography>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={creating || updating} sx={{ bgcolor: NAVY, "&:hover": { bgcolor: "#1a4a7a" } }}>
            {creating || updating ? <CircularProgress size={18} /> : editTarget ? "Save Changes" : "Add Milestone"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
